import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2, Phone, Mail, MapPin, ArrowUpRight } from "lucide-react";
import { PRIVACY_URL, CONTACTS } from "@/data/content";
import { trackMetaLead } from "@/components/MetaPixel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { nome: "", telefono: "", email: "", citta: "", tipologia: "Appartamento", note: "", privacy: false };

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.privacy) {
      toast.error("Devi accettare l'informativa privacy per inviare la richiesta.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/leads`, form);
      toast.success(data.message || "Richiesta inviata con successo!");
      trackMetaLead();
      setForm(initial);
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || err.response?.data?.detail || "Errore nell'invio. Riprova tra poco.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-xl bg-white/5 border border-white/15 px-4 py-3.5 text-base text-white placeholder:text-white/35 focus:outline-none focus:border-azure focus:ring-1 focus:ring-azure transition-[border-color] duration-300";

  return (
    <section id="contatti" data-testid="contact-section" className="bg-navy text-white py-24 lg:py-36 relative overflow-hidden">
      <div className="absolute -bottom-40 right-0 w-[480px] h-[480px] rounded-full bg-azure/15 blur-3xl" />
      <div className="relative max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6 }}
            className="eyebrow text-gold mb-6"
          >
            Contattaci
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7 }}
            className="font-serif font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.12]"
          >
            Hai un immobile da affittare? <span className="italic text-azure">Parliamone.</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="mt-6 text-white/60 leading-relaxed max-w-md"
          >
            Compila il modulo: un nostro consulente ti ricontatterà senza impegno per una valutazione
            gratuita della tua casa a Firenze, Scandicci e dintorni.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: 0.12 }}
            className="mt-8 space-y-4"
            data-testid="contact-info-block"
          >
            <a href={CONTACTS.telHref} data-testid="contact-phone-link" className="flex items-center gap-4 text-white/80 hover:text-white transition-colors duration-300 group">
              <span className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-azure/15 text-azure group-hover:bg-azure group-hover:text-white transition-colors duration-300">
                <Phone size={17} />
              </span>
              <span>
                <span className="block text-xs text-white/45 uppercase tracking-wider">Telefono</span>
                <span className="text-sm font-semibold">{CONTACTS.tel}</span>
              </span>
            </a>
            <a href={`mailto:${CONTACTS.email}`} data-testid="contact-email-link" className="flex items-center gap-4 text-white/80 hover:text-white transition-colors duration-300 group">
              <span className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-azure/15 text-azure group-hover:bg-azure group-hover:text-white transition-colors duration-300">
                <Mail size={17} />
              </span>
              <span>
                <span className="block text-xs text-white/45 uppercase tracking-wider">Email</span>
                <span className="text-sm font-semibold">{CONTACTS.email}</span>
              </span>
            </a>
            <a href={CONTACTS.mapsUrl} target="_blank" rel="noopener noreferrer" data-testid="contact-address-link" className="flex items-center gap-4 text-white/80 hover:text-white transition-colors duration-300 group">
              <span className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-azure/15 text-azure group-hover:bg-azure group-hover:text-white transition-colors duration-300">
                <MapPin size={17} />
              </span>
              <span>
                <span className="block text-xs text-white/45 uppercase tracking-wider">Dove siamo</span>
                <span className="text-sm font-semibold inline-flex items-center gap-1.5">
                  {CONTACTS.address}
                  <ArrowUpRight size={14} className="text-azure" />
                </span>
              </span>
            </a>
          </motion.div>
          <motion.img
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: 0.15 }}
            src="/assets/takes-care-logo.jpg"
            alt="Semplificaffitto.it Takes Care - ci prendiamo cura della tua casa"
            className="mt-10 rounded-full border-4 border-white/20 shadow-2xl w-52 h-52 lg:w-64 lg:h-64 object-cover"
          />
        </div>

        <motion.form
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, delay: 0.1 }}
          onSubmit={submit}
          data-testid="contact-form"
          className="rounded-3xl border border-white/15 bg-ink/40 backdrop-blur-xl p-7 lg:p-10 space-y-5"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <input
              required
              value={form.nome}
              onChange={set("nome")}
              placeholder="Nome e cognome *"
              className={inputCls}
              data-testid="contact-input-name"
            />
            <input
              required
              value={form.telefono}
              onChange={set("telefono")}
              placeholder="Telefono *"
              type="tel"
              className={inputCls}
              data-testid="contact-input-phone"
            />
          </div>
          <input
            required
            value={form.email}
            onChange={set("email")}
            placeholder="Email *"
            type="email"
            className={inputCls}
            data-testid="contact-input-email"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <input
              required
              value={form.citta}
              onChange={set("citta")}
              placeholder="Città dell'immobile *"
              className={inputCls}
              data-testid="contact-input-city"
            />
            <select
              value={form.tipologia}
              onChange={set("tipologia")}
              className={`${inputCls} appearance-none`}
              data-testid="contact-input-type"
            >
              {["Appartamento", "Villa / Villetta", "Altro"].map((t) => (
                <option key={t} value={t} className="text-ink">
                  {t}
                </option>
              ))}
            </select>
          </div>
          <textarea
            value={form.note}
            onChange={set("note")}
            placeholder="Raccontaci il tuo immobile (metratura, zona, canone desiderato...)"
            rows={4}
            className={`${inputCls} resize-none`}
            data-testid="contact-input-notes"
          />

          <label className="flex items-start gap-3 text-xs text-white/55 leading-relaxed cursor-pointer">
            <input
              type="checkbox"
              checked={form.privacy}
              onChange={set("privacy")}
              className="mt-0.5 w-4 h-4 accent-[#009DDF]"
              data-testid="contact-input-privacy"
            />
            <span>
              Ai sensi dell'art. 13 Reg. Europeo 679/16 (GDPR) dichiaro di aver letto ed accettato{" "}
              <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" className="text-azure underline underline-offset-2">
                l'informativa
              </a>
              . *
            </span>
          </label>

          <button
            type="submit"
            disabled={loading}
            data-testid="contact-form-submit-btn"
            className="w-full inline-flex items-center justify-center gap-2 bg-azure hover:bg-brandblue disabled:opacity-60 text-white font-semibold rounded-full px-7 py-4 text-sm transition-colors duration-300"
          >
            {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={16} />}
            Invia richiesta
          </button>
        </motion.form>
      </div>
    </section>
  );
}
